import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiPackage } = FiIcons;

function InventoryManagement() {
  const { user, inventory, updateInventory, addInventoryItem, deleteInventoryItem } = useApp();
  const [editingItem, setEditingItem] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const prisonInventory = inventory[user.prisonId] || [];
  const categories = ['All', ...new Set(prisonInventory.map(item => item.category))];
  
  const filteredItems = selectedCategory === 'All' 
    ? prisonInventory 
    : prisonInventory.filter(item => item.category === selectedCategory);

  const [newItem, setNewItem] = useState({
    name: '',
    category: 'Sandwiches',
    price: '',
    stock: '',
    image: 'https://images.unsplash.com/photo-1553909489-cd47e0ef937f?w=300&h=200&fit=crop'
  });

  const handleEdit = (item) => {
    setEditingItem({ ...item });
    setIsAddingNew(false);
  };

  const handleSave = (item) => {
    updateInventory(user.prisonId, item.id, {
      name: item.name,
      category: item.category,
      price: parseFloat(item.price),
      stock: parseInt(item.stock),
      image: item.image
    });
    setEditingItem(null);
  };

  const handleDelete = (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      deleteInventoryItem(user.prisonId, itemId);
    }
  };

  const handleAddNew = () => {
    if (newItem.name && newItem.price && newItem.stock) {
      addInventoryItem(user.prisonId, {
        ...newItem,
        price: parseFloat(newItem.price),
        stock: parseInt(newItem.stock)
      });
      setNewItem({
        name: '',
        category: 'Sandwiches',
        price: '',
        stock: '',
        image: 'https://images.unsplash.com/photo-1553909489-cd47e0ef937f?w=300&h=200&fit=crop'
      });
      setIsAddingNew(false);
    }
  };

  const EditForm = ({ item, onSave, onCancel }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <input
          type="text"
          value={item.name}
          onChange={(e) => setEditingItem({ ...item, name: e.target.value })}
          className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          placeholder="Item name"
        />
        <select
          value={item.category}
          onChange={(e) => setEditingItem({ ...item, category: e.target.value })}
          className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="Sandwiches">Sandwiches</option>
          <option value="Hot Drinks">Hot Drinks</option>
          <option value="Cold Drinks">Cold Drinks</option>
          <option value="Snacks">Snacks</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <input
          type="number"
          step="0.01"
          value={item.price}
          onChange={(e) => setEditingItem({ ...item, price: e.target.value })}
          className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          placeholder="Price"
        />
        <input
          type="number"
          value={item.stock}
          onChange={(e) => setEditingItem({ ...item, stock: e.target.value })}
          className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          placeholder="Stock"
        />
      </div>
      <input
        type="url"
        value={item.image}
        onChange={(e) => setEditingItem({ ...item, image: e.target.value })}
        className="w-full px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        placeholder="Image URL"
      />
      <div className="flex space-x-2">
        <button
          onClick={() => onSave(item)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          <span>Save</span>
        </button>
        <button
          onClick={onCancel}
          className="flex items-center space-x-2 px-4 py-2 bg-government-600 hover:bg-government-700 text-white rounded-lg font-medium transition-colors"
        >
          <SafeIcon icon={FiX} className="w-4 h-4" />
          <span>Cancel</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-government-900">
            Inventory Management
          </h1>
          <button
            onClick={() => setIsAddingNew(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Add Item</span>
          </button>
        </div>
        
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-government-600 hover:bg-government-50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Add New Item Form */}
      {isAddingNew && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-6"
        >
          <h3 className="text-lg font-bold text-government-900 mb-4">Add New Item</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Item name"
              />
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="Sandwiches">Sandwiches</option>
                <option value="Hot Drinks">Hot Drinks</option>
                <option value="Cold Drinks">Cold Drinks</option>
                <option value="Snacks">Snacks</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="number"
                step="0.01"
                value={newItem.price}
                onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Price"
              />
              <input
                type="number"
                value={newItem.stock}
                onChange={(e) => setNewItem({ ...newItem, stock: e.target.value })}
                className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Stock"
              />
            </div>
            <input
              type="url"
              value={newItem.image}
              onChange={(e) => setNewItem({ ...newItem, image: e.target.value })}
              className="w-full px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Image URL"
            />
            <div className="flex space-x-2">
              <button
                onClick={handleAddNew}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                <span>Add Item</span>
              </button>
              <button
                onClick={() => setIsAddingNew(false)}
                className="flex items-center space-x-2 px-4 py-2 bg-government-600 hover:bg-government-700 text-white rounded-lg font-medium transition-colors"
              >
                <SafeIcon icon={FiX} className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Inventory Items */}
      {filteredItems.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center"
        >
          <SafeIcon icon={FiPackage} className="w-16 h-16 text-government-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-government-900 mb-2">
            No items found
          </h3>
          <p className="text-government-600">
            {selectedCategory === 'All' ? 'No inventory items available' : `No items in ${selectedCategory} category`}
          </p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-48 object-cover"
              />
              
              <div className="p-6">
                {editingItem && editingItem.id === item.id ? (
                  <EditForm
                    item={editingItem}
                    onSave={handleSave}
                    onCancel={() => setEditingItem(null)}
                  />
                ) : (
                  <>
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-government-900 mb-1">
                        {item.name}
                      </h3>
                      <p className="text-sm text-government-600 mb-2">
                        {item.category}
                      </p>
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-primary-600">
                          £{item.price.toFixed(2)}
                        </span>
                        <span className={`font-medium ${
                          item.stock > 10 ? 'text-green-600' : 
                          item.stock > 0 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {item.stock} in stock
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

export default InventoryManagement;